import { useStatsT } from './statsContext'
import { BalanceCard, ChargebackRatio, EarningsSide, OverviewEarningsCard, ReferralCard, SummaryCard, TopBanner, TopFanCard, TopMessageCard, TopPostCard, TopStoryCard, TopStreamCard } from './Cards'
import { SummaryGrid, SummaryItem } from './StatsParts'
import { dig, int, money, type Raw } from './statisticsData'

/** The right column of each view (the CRM's renderer.side()). */

export function StatementsSide() {
  return (
    <>
      <TopBanner />
      <BalanceCard />
      <EarningsSide />
    </>
  )
}

export function ChargebacksSide() {
  return (
    <>
      <TopBanner />
      <BalanceCard extraTop={<ChargebackRatio />} />
      <EarningsSide />
    </>
  )
}

export function ReferralsSide() {
  return (
    <>
      <ReferralCard />
      <EarningsSide />
    </>
  )
}

export function OverviewSide() {
  return (
    <>
      <TopBanner />
      <BalanceCard />
      <OverviewEarningsCard />
    </>
  )
}

export function PostsSide() {
  const { t } = useStatsT()
  return (
    <>
      <SummaryCard by="posts" empty={t('empty.posts')}>
        {(d) => {
          const p = dig(d, 'posts')
          if (!p.hasStatistic) return null
          return (
            <SummaryGrid spark={p.chartData} tone="s3">
              <SummaryItem label={t('l.posts')} value={int(dig(p, 'count').total, false)} change={dig(p, 'count').delta} />
              <SummaryItem label={t('l.postsViews')} value={int(dig(p, 'views').total, false)} change={dig(p, 'views').delta} />
              <SummaryItem label={t('l.postsEarnings')} value={money(dig(p, 'earnings').total)} change={dig(p, 'earnings').delta} />
            </SummaryGrid>
          )
        }}
      </SummaryCard>
      <TopPostCard compact />
    </>
  )
}

export function MessagesSide() {
  const { t } = useStatsT()
  return (
    <>
      <SummaryCard by="messages" empty={t('empty.messages')}>
        {(d) => {
          const m = dig(d, 'massMessages')
          if (!m.hasStatistic) return null
          return (
            <SummaryGrid spark={m.chartData} tone="s4">
              <SummaryItem label={t('l.messages')} value={int(dig(m, 'count').total, false)} change={dig(m, 'count').delta} />
              <SummaryItem label={t('l.messageEarnings')} value={money(dig(m, 'earnings').total)} change={dig(m, 'earnings').delta} />
            </SummaryGrid>
          )
        }}
      </SummaryCard>
      <TopMessageCard />
    </>
  )
}

export function StreamingSide() {
  const { t } = useStatsT()
  return (
    <>
      <SummaryCard by="streams" empty={t('empty.streaming')}>
        {(d) => {
          const s = dig(d, 'streams')
          if (!s.hasStatistic) return null
          return (
            <SummaryGrid spark={s.chartData} tone="s6">
              <SummaryItem label={t('l.streams')} value={String(dig(s, 'count').total ?? '0m')} change={dig(s, 'count').delta} />
              <SummaryItem label={t('l.viewers')} value={int(dig(s, 'views').total)} change={dig(s, 'views').delta} />
              <SummaryItem label={t('l.streamsEarnings')} value={money(dig(s, 'earnings').total)} change={dig(s, 'earnings').delta} />
            </SummaryGrid>
          )
        }}
      </SummaryCard>
      <TopStreamCard />
    </>
  )
}

export function StoriesSide() {
  const { t } = useStatsT()
  return (
    <>
      <SummaryCard by="story" empty={t('empty.stories')}>
        {(d) => {
          const s = dig(d, 'stories')
          if (!s.hasStatistic) return null
          return (
            <SummaryGrid spark={s.chartData} tone="s2">
              <SummaryItem label={t('l.stories')} value={int(dig(s, 'count').total, false)} change={dig(s, 'count').delta} />
              <SummaryItem label={t('l.views')} value={int(dig(s, 'views').total, false)} change={dig(s, 'views').delta} />
              <SummaryItem label={t('l.storiesTips')} value={money(dig(s, 'tips').total)} change={dig(s, 'tips').delta} />
            </SummaryGrid>
          )
        }}
      </SummaryCard>
      <TopStoryCard />
    </>
  )
}

export function VisitorsSide() {
  const { t } = useStatsT()
  return (
    <SummaryCard by="visitors">
      {(d) => {
        const v = dig(d, 'visitors')
        if (!v.hasStatistic) return null
        return (
          <SummaryGrid spark={v.chartData} tone="s1">
            <SummaryItem label={t('l.profileVisitors')} value={int(dig(v, 'visitors').total, false)} change={dig(v, 'visitors').delta} />
            <SummaryItem
              label={t('l.newSubsRenews')}
              value={`${int(dig(v, 'subscriptions', 'new').total, false)} / ${int(dig(v, 'subscriptions', 'renew').total, false)}`}
              change={dig(v, 'subscriptions', 'new').delta}
            />
            <SummaryItem label={t('l.subscriptionsEarnings')} value={money(dig(v, 'earnings').total)} change={dig(v, 'earnings').delta} />
          </SummaryGrid>
        )
      }}
    </SummaryCard>
  )
}

function OfferSummary({ by, field, empty }: { by: string; field: string; empty: string }) {
  const { t } = useStatsT()
  return (
    <SummaryCard by={by} empty={empty}>
      {(d: Raw) => {
        const p = dig(d, field)
        if (!p.hasStatistic) return null
        return (
          <SummaryGrid spark={p.chartData} tone="s1">
            <SummaryItem label={t('l.offers')} value={int(dig(p, 'offers').total, false)} change={dig(p, 'offers').delta} />
            <SummaryItem label={t('l.claims')} value={int(dig(p, 'claims').total, false)} change={dig(p, 'claims').delta} />
            <SummaryItem label={t('l.earnings')} value={money(dig(p, 'earnings').total)} change={dig(p, 'earnings').delta} />
          </SummaryGrid>
        )
      }}
    </SummaryCard>
  )
}

export function PromotionsSide() {
  const { t } = useStatsT()
  return <OfferSummary by="promo" field="promo" empty={t('empty.promotions')} />
}

export function TrialsSide() {
  const { t } = useStatsT()
  return <OfferSummary by="trials" field="trials" empty={t('empty.trials')} />
}

export function TrackingSide() {
  const { t } = useStatsT()
  return (
    <SummaryCard by="campaign" empty={t('empty.tracking')}>
      {(d) => {
        const c = dig(d, 'campaigns')
        if (!c.hasStatistic) return null
        return (
          <SummaryGrid spark={c.chartData} tone="s1">
            <SummaryItem label={t('l.trackingLinks')} value={int(dig(c, 'count').total, false)} change={dig(c, 'count').delta} />
            <SummaryItem label={t('l.clicks')} value={int(dig(c, 'clicks').total, false)} change={dig(c, 'clicks').delta} />
            <SummaryItem label={t('l.subscribers')} value={int(dig(c, 'claims').total, false)} change={dig(c, 'claims').delta} />
          </SummaryGrid>
        )
      }}
    </SummaryCard>
  )
}

export function FansSide() {
  const { t } = useStatsT()
  return (
    <>
      <SummaryCard by="fans">
        {(d) => {
          const f = dig(d, 'fans')
          if (!f.hasStatistic) return null
          return (
            <SummaryGrid spark={f.chartData} tone="s1">
              <SummaryItem label={t('l.subscribers')} value={int(dig(f, 'subscribers').total, false)} change={dig(f, 'subscribers').delta} />
              <SummaryItem label={t('l.newSubsRenews')} value={`${int(dig(f, 'subscriptions', 'new').total, false)} / ${int(dig(f, 'subscriptions', 'renew').total, false)}`} />
              <SummaryItem label={t('l.subscriptionsEarnings')} value={money(dig(f, 'earnings').total)} change={dig(f, 'earnings').delta} />
            </SummaryGrid>
          )
        }}
      </SummaryCard>
      <TopFanCard full />
    </>
  )
}
